import { DEFAULT_ECOPACK_IMAGE_URL } from '@/constants';
import type { Product } from '@/pages/store/product-detail.types';

interface ProductInfoProps {
  product: Product;
  quantity: number;
  availabilityStatus: {
    text: string;
    className: string;
    isAvailable: boolean;
    reason: string;
  };
  onQuantityChange: (quantity: number) => void;
}

function getOrderTypeText(type: Product['discount_type']) {
  switch (type) {
    case 'DEADLINE_DISCOUNT':
      return '마감할인';
    case 'RESERVATION_DISCOUNT':
      return '예약할인';
    case 'REGULAR_ORDER':
      return '일반주문';
    default:
      return type;
  }
}

export function ProductInfo({ product, quantity, availabilityStatus, onQuantityChange }: ProductInfoProps) {
  const totalPrice = product.discounted_price * quantity;
  const productImageUrl = product.image_url || (product.discount_type === 'DEADLINE_DISCOUNT' ? DEFAULT_ECOPACK_IMAGE_URL : undefined);

  return (
    <>
      {/* 상품 이미지 */}
      <div className="relative w-full h-64 bg-gray-100">
        {productImageUrl ? (
          <img
            src={productImageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <span className="text-gray-500">이미지 없음</span>
          </div>
        )}
      </div>

      {/* 상품 정보 */}
      <div className="px-4 py-6">
        <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
              {getOrderTypeText(product.discount_type)}
            </span>
            <span className={`text-sm px-2 py-1 rounded ${availabilityStatus.className}`}>
              {availabilityStatus.text}
            </span>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{product.name}</h1>
          
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {product.discount_type === 'RESERVATION_DISCOUNT' ? (
                <span className="text-green-600 font-medium">예약 가능</span>
              ) : (
                <span className={`
                  ${product.stock_quantity === 0 ? 'text-red-600 font-bold' : ''}
                  ${product.stock_quantity === 1 ? 'text-red-500' : ''}
                  ${product.stock_quantity > 1 && product.stock_quantity < 5 ? 'text-yellow-600' : ''}
                `}>
                  {product.stock_quantity === 0 ? '품절' : `재고 ${product.stock_quantity}개`}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2 text-right">
              {product.discount_rate > 0 && (
                <>
                  <span className="text-lg text-gray-500 line-through">
                    {product.original_price.toLocaleString()}원
                  </span>
                  <span className="text-sm font-bold text-red-600">
                    {product.discount_rate}% 할인
                  </span>
                </>
              )}
              <span className="text-2xl font-bold text-green-600">
                {product.discounted_price.toLocaleString()}원
              </span>
            </div>
          </div>
        </div>

        {/* 상품 설명 */}
        {product.description && (
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h3 className="font-semibold mb-2">상품 설명</h3>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{product.description}</p>
          </div>
        )}

        {/* 수량 선택 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold">수량 선택</h3>
            <div className={`flex items-center border border-gray-300 rounded-lg ${
              !availabilityStatus.isAvailable ? 'opacity-50' : ''
            }`}>
              <button
                onClick={() => onQuantityChange(quantity - 1)}
                className="p-2 hover:bg-gray-100 transition-colors disabled:opacity-50"
                disabled={quantity <= 1 || !availabilityStatus.isAvailable}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <span className="px-4 py-2 min-w-[60px] text-center">{quantity}</span>
              <button
                onClick={() => onQuantityChange(quantity + 1)}
                className="p-2 hover:bg-gray-100 transition-colors disabled:opacity-50"
                disabled={(product.discount_type !== 'RESERVATION_DISCOUNT' && quantity >= product.stock_quantity) || !availabilityStatus.isAvailable}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-gray-900">
              총 {totalPrice.toLocaleString()}원
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
